import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, Search, ChevronDown, LogOut, User } from 'lucide-react';
import type { CSSProperties } from 'react';

const ROUTE_TITLES: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/scans': 'Scans',
  '/competitors': 'Competitors',
  '/team': 'Team',
  '/api-keys': 'API Keys',
  '/settings': 'Settings',
};

function getPageTitle(pathname: string): string {
  return ROUTE_TITLES[pathname] ?? 'Dashboard';
}

export interface HeaderProps {
  onToggleSidebar: () => void;
  userName?: string;
  userEmail?: string;
  userAvatarUrl?: string;
  onLogout?: () => void;
  showSearch?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
}

export function Header({
  onToggleSidebar,
  userName = 'User',
  userEmail = 'user@example.com',
  userAvatarUrl,
  onLogout,
  showSearch = false,
  searchValue = '',
  onSearchChange,
}: HeaderProps) {
  const location = useLocation();
  const pageTitle = getPageTitle(location.pathname);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [hamburgerHovered, setHamburgerHovered] = useState(false);
  const [avatarBtnHovered, setAvatarBtnHovered] = useState(false);
  const [profileHovered, setProfileHovered] = useState(false);
  const [logoutHovered, setLogoutHovered] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const closeDropdown = useCallback(() => {
    setDropdownOpen(false);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        closeDropdown();
      }
    }

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen, closeDropdown]);

  // Close dropdown on Escape key
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        closeDropdown();
      }
    }

    if (dropdownOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [dropdownOpen, closeDropdown]);

  const initials = userName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  /* ---- Styles ---- */

  const headerStyle: CSSProperties = {
    display: 'flex',
    height: 64,
    flexShrink: 0,
    alignItems: 'center',
    gap: 16,
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    backgroundColor: '#0d0d14',
    padding: '0 16px',
  };

  // NOTE: No `display` here — Tailwind `flex md:hidden` controls visibility
  const hamburgerStyle: CSSProperties = {
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: 8,
    border: 'none',
    backgroundColor: hamburgerHovered ? 'rgba(255,255,255,0.05)' : 'transparent',
    color: hamburgerHovered ? '#ffffff' : 'rgba(255,255,255,0.7)',
    cursor: 'pointer',
    transition: 'background-color 0.15s, color 0.15s',
    padding: 0,
  };

  const titleStyle: CSSProperties = {
    fontSize: 18,
    fontWeight: 600,
    color: '#ffffff',
    margin: 0,
  };

  const spacerStyle: CSSProperties = {
    flex: 1,
  };

  const searchContainerStyle: CSSProperties = {
    alignItems: 'center',
    gap: 8,
    borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.05)',
    padding: '8px 12px',
  };

  const searchInputStyle: CSSProperties = {
    backgroundColor: 'transparent',
    fontSize: 14,
    color: '#ffffff',
    border: 'none',
    outline: 'none',
    width: 192,
  };

  const avatarBtnStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    borderRadius: 8,
    padding: 6,
    border: 'none',
    backgroundColor: avatarBtnHovered ? 'rgba(255,255,255,0.05)' : 'transparent',
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  };

  const avatarCircleStyle: CSSProperties = {
    display: 'flex',
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: '#6366f1',
  };

  const avatarImgStyle: CSSProperties = {
    width: 32,
    height: 32,
    borderRadius: '50%',
    objectFit: 'cover',
  };

  const chevronStyle: CSSProperties = {
    width: 16,
    height: 16,
    color: 'rgba(255,255,255,0.5)',
    transition: 'transform 0.2s',
    transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
  };

  const dropdownStyle: CSSProperties = {
    position: 'absolute',
    right: 0,
    top: '100%',
    zIndex: 50,
    marginTop: 8,
    width: 256,
    borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.1)',
    backgroundColor: '#18181f',
    boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
  };

  const dropdownUserSectionStyle: CSSProperties = {
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    padding: '12px 16px',
  };

  const dropdownUserInnerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  };

  const dropdownAvatarStyle: CSSProperties = {
    display: 'flex',
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: '#6366f1',
    flexShrink: 0,
  };

  const dropdownAvatarImgStyle: CSSProperties = {
    width: 40,
    height: 40,
    borderRadius: '50%',
    objectFit: 'cover',
  };

  const userInfoContainerStyle: CSSProperties = {
    minWidth: 0,
  };

  const userNameStyle: CSSProperties = {
    fontSize: 14,
    fontWeight: 500,
    color: '#ffffff',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    margin: 0,
  };

  const userEmailStyle: CSSProperties = {
    fontSize: 12,
    color: 'rgba(255,255,255,0.5)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    margin: 0,
  };

  const menuSectionStyle: CSSProperties = {
    padding: '4px 0',
  };

  const menuItemBaseStyle: CSSProperties = {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    gap: 12,
    padding: '10px 16px',
    fontSize: 14,
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transition: 'background-color 0.15s, color 0.15s',
    textAlign: 'left' as const,
  };

  const profileItemStyle: CSSProperties = {
    ...menuItemBaseStyle,
    color: profileHovered ? '#ffffff' : 'rgba(255,255,255,0.7)',
    backgroundColor: profileHovered ? 'rgba(255,255,255,0.05)' : 'transparent',
  };

  const logoutItemStyle: CSSProperties = {
    ...menuItemBaseStyle,
    color: '#ef4444',
    backgroundColor: logoutHovered ? 'rgba(255,255,255,0.05)' : 'transparent',
  };

  return (
    <header style={headerStyle}>
      {/* Mobile hamburger - Tailwind `flex md:hidden` controls display */}
      <button
        type="button"
        onClick={onToggleSidebar}
        className="flex md:hidden"
        style={hamburgerStyle}
        onMouseEnter={() => setHamburgerHovered(true)}
        onMouseLeave={() => setHamburgerHovered(false)}
        aria-label="Toggle sidebar"
      >
        <Menu style={{ width: 20, height: 20 }} />
      </button>

      {/* Page title */}
      <h1 style={titleStyle}>{pageTitle}</h1>

      {/* Spacer */}
      <div style={spacerStyle} />

      {/* Search input (optional) - keep Tailwind for responsive visibility */}
      {showSearch && (
        <div className="hidden sm:flex" style={searchContainerStyle}>
          <Search style={{ width: 16, height: 16, color: 'rgba(255,255,255,0.5)' }} />
          <input
            type="text"
            placeholder="Search scans..."
            value={searchValue}
            onChange={(e) => onSearchChange?.(e.target.value)}
            style={searchInputStyle}
          />
        </div>
      )}

      {/* User avatar dropdown */}
      <div style={{ position: 'relative' }} ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setDropdownOpen((prev) => !prev)}
          style={avatarBtnStyle}
          onMouseEnter={() => setAvatarBtnHovered(true)}
          onMouseLeave={() => setAvatarBtnHovered(false)}
          aria-label="User menu"
          aria-expanded={dropdownOpen}
          aria-haspopup="menu"
        >
          {userAvatarUrl ? (
            <img src={userAvatarUrl} alt={userName} style={avatarImgStyle} />
          ) : (
            <div style={avatarCircleStyle}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#ffffff' }}>{initials}</span>
            </div>
          )}
          {/* ChevronDown hidden on small screens - keep Tailwind for responsive */}
          <ChevronDown className="hidden sm:block" style={chevronStyle} />
        </button>

        {/* Dropdown menu */}
        {dropdownOpen && (
          <div style={dropdownStyle} role="menu">
            <div style={dropdownUserSectionStyle}>
              <div style={dropdownUserInnerStyle}>
                {userAvatarUrl ? (
                  <img src={userAvatarUrl} alt={userName} style={dropdownAvatarImgStyle} />
                ) : (
                  <div style={dropdownAvatarStyle}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#ffffff' }}>{initials}</span>
                  </div>
                )}
                <div style={userInfoContainerStyle}>
                  <p style={userNameStyle}>{userName}</p>
                  <p style={userEmailStyle}>{userEmail}</p>
                </div>
              </div>
            </div>
            <div style={menuSectionStyle}>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  closeDropdown();
                }}
                style={profileItemStyle}
                onMouseEnter={() => setProfileHovered(true)}
                onMouseLeave={() => setProfileHovered(false)}
              >
                <User style={{ width: 16, height: 16 }} />
                <span>Profile</span>
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  closeDropdown();
                  onLogout?.();
                }}
                style={logoutItemStyle}
                onMouseEnter={() => setLogoutHovered(true)}
                onMouseLeave={() => setLogoutHovered(false)}
              >
                <LogOut style={{ width: 16, height: 16 }} />
                <span>Log out</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
